import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { EmpresaConfig } from '../types/empresa';

/**
 * Utilitário para formatar a data atual por extenso
 */
function getDataEmissaoFormatada(): string {
  const agora = new Date();
  const meses = [
    'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
    'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'
  ];
  const dia = String(agora.getDate()).padStart(2, '0');
  const mes = meses[agora.getMonth()];
  const ano = agora.getFullYear();
  const hora = String(agora.getHours()).padStart(2, '0');
  const min = String(agora.getMinutes()).padStart(2, '0');

  return `${dia} de ${mes} de ${ano} às ${hora}:${min}`;
}

/**
 * Cria o elemento HTML da ficha cadastral em padrão formal A4
 */
function criarHtmlFicha(empresa: EmpresaConfig): string {
  const dataEmissao = getDataEmissaoFormatada();
  const nomeFantasia = empresa.nome_fantasia || empresa.razao_social || 'Empresa';
  const razaoSocial = empresa.razao_social || 'Razão Social não informada';
  const cnpj = empresa.cnpj || 'Não informado';
  const ie = empresa.inscricao_estadual || 'Isento / Não informado';
  const im = empresa.inscricao_municipal || 'Não informada';
  const antt = empresa.rntrc_antt || 'Não informado';
  const cnae = empresa.cnae_principal || '49.30-2-02 - Transporte rodoviário de carga';
  const regime = empresa.regime_tributario || 'Simples Nacional';

  const telefone = empresa.telefone || 'Não informado';
  const whatsapp = empresa.whatsapp || 'Não informado';
  const email = empresa.email || 'Não informado';
  const emailFinanceiro = empresa.email_financeiro || email;
  const emailOperacional = empresa.email_operacional || email;
  const website = empresa.website || 'www.voope.com.br';

  const enderecoLinha1 = `${empresa.logradouro || 'Logradouro não informado'}, nº ${empresa.numero || 'S/N'}${empresa.complemento ? ` (${empresa.complemento})` : ''}`;
  const enderecoLinha2 = `${empresa.bairro || 'Bairro'} • ${empresa.cidade || 'Cidade'} - ${empresa.uf || 'UF'} • CEP: ${empresa.cep || '00000-000'}`;

  const banco = empresa.banco_padrao || 'Não informado';
  const agencia = empresa.agencia || 'Não informado';
  const conta = empresa.conta_corrente || 'Não informado';
  const chavePix = empresa.chave_pix || cnpj;
  const tipoPix = empresa.tipo_chave_pix ? empresa.tipo_chave_pix.toUpperCase() : 'CNPJ';
  const titular = empresa.titular_conta || razaoSocial;

  const rodape = empresa.rodape_documentos || `${razaoSocial} • CNPJ: ${cnpj} • ${telefone}`;
  const observacoes = empresa.observacoes_padrao || 'Documento cadastral emitido para fins de validação jurídica, contratos comerciais, faturamento e cadastro de fornecedor / transportador parceiro.';

  return `
    <div id="ficha-cadastral-a4" style="
      width: 800px;
      min-height: 1120px;
      padding: 40px 45px;
      background-color: #ffffff;
      color: #0f172a;
      font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, Helvetica, Arial, sans-serif;
      box-sizing: border-box;
      position: relative;
    ">
      <!-- CABEÇALHO COM LOGO E TÍTULO -->
      <div style="display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #dc2626; padding-bottom: 18px; margin-bottom: 22px;">
        <div style="display: flex; align-items: center; gap: 16px;">
          ${empresa.logo_url ? `
            <img 
              src="${empresa.logo_url}" 
              alt="${nomeFantasia}" 
              style="max-height: 52px; max-width: 170px; object-contain: contain;"
              crossorigin="anonymous"
            />
          ` : `
            <div style="background-color: #dc2626; color: #ffffff; padding: 10px 14px; border-radius: 8px; font-weight: 900; font-size: 16px; letter-spacing: 0.5px;">
              VOOPE
            </div>
          `}
          <div>
            <div style="font-size: 18px; font-weight: 800; color: #0f172a; text-transform: uppercase; letter-spacing: -0.2px;">
              ${nomeFantasia}
            </div>
            <div style="font-size: 11px; font-weight: 600; color: #dc2626; text-transform: uppercase; letter-spacing: 0.8px;">
              Ficha Cadastral Oficial da Empresa
            </div>
          </div>
        </div>

        <div style="text-align: right;">
          <div style="font-size: 9px; color: #64748b; text-transform: uppercase; font-weight: 700; letter-spacing: 0.5px;">Emissão Oficial</div>
          <div style="font-size: 11px; font-weight: 600; color: #1e293b;">${dataEmissao}</div>
          <div style="font-size: 9px; color: #16a34a; font-weight: 700; margin-top: 2px;">● Cadastro Ativo & Regular</div>
        </div>
      </div>

      <!-- BLOCO 1: DADOS JURÍDICOS E FISCAIS -->
      <div style="margin-bottom: 18px;">
        <div style="font-size: 11px; font-weight: 800; text-transform: uppercase; color: #dc2626; letter-spacing: 0.8px; margin-bottom: 8px; display: flex; align-items: center; gap: 6px;">
          <span style="display: inline-block; width: 6px; height: 6px; background-color: #dc2626; border-radius: 50%;"></span>
          1. Identificação Jurídica & Fiscal
        </div>
        <table style="width: 100%; border-collapse: collapse; font-size: 11px;">
          <tbody>
            <tr style="border-bottom: 1px solid #e2e8f0;">
              <td style="padding: 7px 10px; background-color: #f8fafc; font-weight: 700; width: 25%; color: #475569;">Razão Social:</td>
              <td style="padding: 7px 10px; font-weight: 800; color: #0f172a; width: 75%;">${razaoSocial}</td>
            </tr>
            <tr style="border-bottom: 1px solid #e2e8f0;">
              <td style="padding: 7px 10px; background-color: #f8fafc; font-weight: 700; color: #475569;">Nome Fantasia:</td>
              <td style="padding: 7px 10px; font-weight: 700; color: #0f172a;">${nomeFantasia}</td>
            </tr>
            <tr style="border-bottom: 1px solid #e2e8f0;">
              <td style="padding: 7px 10px; background-color: #f8fafc; font-weight: 700; color: #475569;">CNPJ:</td>
              <td style="padding: 7px 10px; font-weight: 800; color: #0f172a;">
                <span style="font-family: monospace; font-size: 12px; background-color: #f1f5f9; padding: 2px 6px; border-radius: 4px;">${cnpj}</span>
              </td>
            </tr>
            <tr style="border-bottom: 1px solid #e2e8f0;">
              <td style="padding: 7px 10px; background-color: #f8fafc; font-weight: 700; color: #475569;">Inscrição Estadual (IE):</td>
              <td style="padding: 7px 10px; color: #1e293b;">${ie}</td>
            </tr>
            <tr style="border-bottom: 1px solid #e2e8f0;">
              <td style="padding: 7px 10px; background-color: #f8fafc; font-weight: 700; color: #475569;">Inscrição Municipal (IM):</td>
              <td style="padding: 7px 10px; color: #1e293b;">${im}</td>
            </tr>
            <tr style="border-bottom: 1px solid #e2e8f0;">
              <td style="padding: 7px 10px; background-color: #f8fafc; font-weight: 700; color: #475569;">Registro ANTT / RNTRC:</td>
              <td style="padding: 7px 10px; font-weight: 800; color: #dc2626;">${antt}</td>
            </tr>
            <tr style="border-bottom: 1px solid #e2e8f0;">
              <td style="padding: 7px 10px; background-color: #f8fafc; font-weight: 700; color: #475569;">CNAE Principal:</td>
              <td style="padding: 7px 10px; color: #1e293b;">${cnae}</td>
            </tr>
            <tr>
              <td style="padding: 7px 10px; background-color: #f8fafc; font-weight: 700; color: #475569;">Regime Tributário:</td>
              <td style="padding: 7px 10px; color: #1e293b;">${regime}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- BLOCO 2: LOCALIZAÇÃO E ENDEREÇO -->
      <div style="margin-bottom: 18px;">
        <div style="font-size: 11px; font-weight: 800; text-transform: uppercase; color: #dc2626; letter-spacing: 0.8px; margin-bottom: 8px; display: flex; align-items: center; gap: 6px;">
          <span style="display: inline-block; width: 6px; height: 6px; background-color: #dc2626; border-radius: 50%;"></span>
          2. Endereço Sede & Operacional
        </div>
        <div style="background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 10px 14px; font-size: 11px; line-height: 1.6;">
          <div style="font-weight: 700; color: #0f172a; font-size: 12px;">${enderecoLinha1}</div>
          <div style="color: #475569; font-weight: 500;">${enderecoLinha2}</div>
          ${empresa.codigo_ibge ? `<div style="font-size: 10px; color: #94a3b8; margin-top: 2px;">Código IBGE Município: ${empresa.codigo_ibge}</div>` : ''}
        </div>
      </div>

      <!-- BLOCO 3: CANAIS DE COMUNICAÇÃO -->
      <div style="margin-bottom: 18px;">
        <div style="font-size: 11px; font-weight: 800; text-transform: uppercase; color: #dc2626; letter-spacing: 0.8px; margin-bottom: 8px; display: flex; align-items: center; gap: 6px;">
          <span style="display: inline-block; width: 6px; height: 6px; background-color: #dc2626; border-radius: 50%;"></span>
          3. Canais Oficiais de Contato & Departamentos
        </div>
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; font-size: 11px;">
          <div style="border: 1px solid #e2e8f0; border-radius: 6px; padding: 8px 12px; background-color: #ffffff;">
            <div style="font-size: 9px; text-transform: uppercase; color: #64748b; font-weight: 700;">Telefone Comercial</div>
            <div style="font-weight: 700; color: #0f172a; margin-top: 2px;">${telefone}</div>
          </div>
          <div style="border: 1px solid #e2e8f0; border-radius: 6px; padding: 8px 12px; background-color: #ffffff;">
            <div style="font-size: 9px; text-transform: uppercase; color: #16a34a; font-weight: 700;">WhatsApp Oficial</div>
            <div style="font-weight: 700; color: #15803d; margin-top: 2px;">${whatsapp}</div>
          </div>
          <div style="border: 1px solid #e2e8f0; border-radius: 6px; padding: 8px 12px; background-color: #ffffff;">
            <div style="font-size: 9px; text-transform: uppercase; color: #64748b; font-weight: 700;">E-mail Geral</div>
            <div style="font-weight: 600; color: #0f172a; margin-top: 2px;">${email}</div>
          </div>
          <div style="border: 1px solid #e2e8f0; border-radius: 6px; padding: 8px 12px; background-color: #ffffff;">
            <div style="font-size: 9px; text-transform: uppercase; color: #64748b; font-weight: 700;">E-mail Financeiro</div>
            <div style="font-weight: 600; color: #0f172a; margin-top: 2px;">${emailFinanceiro}</div>
          </div>
          <div style="border: 1px solid #e2e8f0; border-radius: 6px; padding: 8px 12px; background-color: #ffffff;">
            <div style="font-size: 9px; text-transform: uppercase; color: #64748b; font-weight: 700;">E-mail Operacional</div>
            <div style="font-weight: 600; color: #0f172a; margin-top: 2px;">${emailOperacional}</div>
          </div>
          <div style="border: 1px solid #e2e8f0; border-radius: 6px; padding: 8px 12px; background-color: #ffffff;">
            <div style="font-size: 9px; text-transform: uppercase; color: #64748b; font-weight: 700;">Portal / Website</div>
            <div style="font-weight: 600; color: #0284c7; margin-top: 2px;">${website}</div>
          </div>
        </div>
      </div>

      <!-- BLOCO 4: DADOS BANCÁRIOS & CHAVE PIX -->
      <div style="margin-bottom: 18px;">
        <div style="font-size: 11px; font-weight: 800; text-transform: uppercase; color: #dc2626; letter-spacing: 0.8px; margin-bottom: 8px; display: flex; align-items: center; gap: 6px;">
          <span style="display: inline-block; width: 6px; height: 6px; background-color: #dc2626; border-radius: 50%;"></span>
          4. Dados para Faturamento, Depósitos & PIX
        </div>
        <div style="border: 1px solid #cbd5e1; border-radius: 6px; overflow: hidden; font-size: 11px;">
          <table style="width: 100%; border-collapse: collapse;">
            <tbody>
              <tr style="border-bottom: 1px solid #e2e8f0;">
                <td style="padding: 7px 10px; background-color: #f8fafc; font-weight: 700; width: 25%; color: #475569;">Instituição Bancária:</td>
                <td style="padding: 7px 10px; font-weight: 700; color: #0f172a; width: 75%;">${banco}</td>
              </tr>
              <tr style="border-bottom: 1px solid #e2e8f0;">
                <td style="padding: 7px 10px; background-color: #f8fafc; font-weight: 700; color: #475569;">Agência / Conta:</td>
                <td style="padding: 7px 10px; font-weight: 700; color: #0f172a;">Agência: ${agencia} &nbsp;|&nbsp; Conta Corrente: ${conta}</td>
              </tr>
              <tr style="border-bottom: 1px solid #e2e8f0;">
                <td style="padding: 7px 10px; background-color: #f8fafc; font-weight: 700; color: #475569;">Chave PIX (${tipoPix}):</td>
                <td style="padding: 7px 10px; font-weight: 800; color: #15803d; font-family: monospace; font-size: 12px;">${chavePix}</td>
              </tr>
              <tr>
                <td style="padding: 7px 10px; background-color: #f8fafc; font-weight: 700; color: #475569;">Favorecido / Titular:</td>
                <td style="padding: 7px 10px; font-weight: 600; color: #1e293b;">${titular}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- BLOCO 5: OBSERVAÇÕES E POLÍTICAS -->
      <div style="margin-bottom: 22px;">
        <div style="font-size: 10px; font-weight: 700; text-transform: uppercase; color: #64748b; letter-spacing: 0.5px; margin-bottom: 6px;">
          Observações e Termos Gerais
        </div>
        <div style="background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 9px 12px; font-size: 10px; color: #475569; line-height: 1.5;">
          ${observacoes}
        </div>
      </div>

      <!-- ASSINATURA & RODAPÉ OFICIAL -->
      <div style="margin-top: 30px; border-top: 1px solid #e2e8f0; padding-top: 16px; display: flex; justify-content: space-between; align-items: flex-end;">
        <div style="font-size: 9px; color: #94a3b8; max-width: 480px; line-height: 1.4;">
          ${rodape}
          <div style="margin-top: 3px; font-size: 8px; color: #cbd5e1;">
            Documento emitido eletronicamente pelo Sistema de Gestão Voope TWO • Autenticidade garantida por CNPJ e RNTRC ativos.
          </div>
        </div>

        <div style="text-align: center; width: 220px;">
          <div style="border-bottom: 1px solid #94a3b8; height: 28px; margin-bottom: 4px;"></div>
          <div style="font-size: 10px; font-weight: 700; color: #0f172a;">${razaoSocial}</div>
          <div style="font-size: 8px; color: #64748b; text-transform: uppercase;">Departamento de Cadastro & Contratos</div>
        </div>
      </div>
    </div>
  `;
}

/**
 * Gera e realiza o download direto da Ficha Cadastral em PDF
 */
export async function baixarFichaCadastralPdf(empresa: EmpresaConfig): Promise<{ success: boolean; error?: string }> {
  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.left = '-9999px';
  container.style.top = '0';
  container.style.width = '800px';
  container.style.zIndex = '-1000';
  container.innerHTML = criarHtmlFicha(empresa);

  document.body.appendChild(container);

  try {
    const element = document.getElementById('ficha-cadastral-a4');
    if (!element) {
      throw new Error('Elemento da ficha cadastral não foi encontrado.');
    }

    // Renderiza em alta resolução com html2canvas
    const canvas = await html2canvas(element, {
      scale: 2.2, // Garante nitidez cristalina das fontes e bordas
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      allowTaint: true
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    const pdfWidth = 210;
    const pdfHeight = 297;

    pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight, undefined, 'FAST');

    const nomeFormatado = (empresa.nome_fantasia || empresa.razao_social || 'Empresa')
      .replace(/[^a-zA-Z0-9_-]/g, '_')
      .replace(/_+/g, '_');

    pdf.save(`Ficha_Cadastral_${nomeFormatado}.pdf`);

    return { success: true };
  } catch (err: any) {
    console.error('[fichaCadastralPdfGenerator] Erro ao gerar PDF:', err);
    return { success: false, error: err.message || 'Erro ao gerar PDF da ficha cadastral.' };
  } finally {
    if (document.body.contains(container)) {
      document.body.removeChild(container);
    }
  }
}

/**
 * Abre a janela de impressão limpa e isolada contendo exclusivamente a Ficha Cadastral
 */
export function imprimirFichaCadastral(empresa: EmpresaConfig): void {
  const htmlConteudo = criarHtmlFicha(empresa);
  
  // Criar iframe oculto de impressão dedicado para não ser afetado pela UI do app ou do iframe pai
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';

  document.body.appendChild(iframe);

  const doc = iframe.contentWindow?.document;
  if (!doc) {
    window.print();
    return;
  }

  doc.open();
  doc.write(`
    <!DOCTYPE html>
    <html lang="pt-BR">
    <head>
      <meta charset="UTF-8" />
      <title>Ficha Cadastral - ${empresa.nome_fantasia || empresa.razao_social}</title>
      <style>
        @page {
          size: A4 portrait;
          margin: 10mm;
        }
        body {
          margin: 0;
          padding: 0;
          background-color: #ffffff;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        @media print {
          body {
            width: 100%;
          }
        }
      </style>
    </head>
    <body>
      ${htmlConteudo}
      <script>
        window.onload = function() {
          setTimeout(function() {
            window.focus();
            window.print();
          }, 300);
        };
      </script>
    </body>
    </html>
  `);
  doc.close();

  // Remove o iframe temporário após impressão
  setTimeout(() => {
    if (document.body.contains(iframe)) {
      document.body.removeChild(iframe);
    }
  }, 30000);
}
